<template>
  <div class="contract">
    <section-title
      class="mt-4 mb-3"
      has-line
      title="Documentos de Contratación"
      title-class="section-title-info"
    />
    <div v-for="(document, idx) in filteredDocuments" :key="idx">
      <div
        class="document__container bg-medium-gray row justify-content-between align-items-center rounded mx-1 mb-4 p-3"
      >
        <div class="col-12 col-md-3">
          <span class="text-gray">Documento:</span>
          <p class="content bold">{{ document.name }}</p>
        </div>
        <div v-if="document.requireDate" class="col-3 flex flex-column">
          <span class="text-gray">Fecha:</span>
          <p class="content">
            <custom-date-picker
              v-model="document.date"
              class="custom-datetime-picker-container"
              :placeholder="'DD/MM/YYYY'"
            />
          </p>
        </div>
        <div class="col-2 h-100 row align-items-center">
          <div>
            <action-button
              v-if="document.date"
              class="d-inline"
              i="fas fa-file-signature"
              text="Descargar para firma"
              variant="outline-success"
              @click="downloadDocument(document)"
            />
          </div>
        </div>
        <div class="col-4 row">
          <div
            v-if="document.attachment || document.attachmentId"
            class="flex flex-column"
          >
            <span class="text-gray">Nombre de Archivo:</span>
            <p>{{ document.attachmentName }}</p>
          </div>
          <div class="document-info__description__actions ml-auto">
            <action-button
              v-if="document.attachment || document.attachmentId"
              class="mr-2 px-3 py-2 show-icon"
              i="fas fa-file-download"
              variant="success"
              @click="downloadSignedDocument(document)"
            />
            <action-button
              v-if="document.attachment || document.attachmentId"
              class="mr-2 px-3 py-2 show-icon"
              i="fas fa-times-circle"
              variant="outline-danger"
              @click="removeFile(document)"
            />
            <custom-file-input
              v-show="!document.attachment && !document.attachmentId"
              :ref="`fileUpload-${document.typeId}`"
              v-model="document.attachment"
              accept=".pdf"
              :attachment-id="document.attachmentId"
              class="custom-upload-document"
              display-style="normal"
              :max-size="100"
              :name="document.attachmentName"
              @input="onUploadDocument($event, document.typeId)"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import SectionTitle from 'SectionTitle';
import _generalApi from '@/modules/app/api';
import CustomFileInput from '@/components/common/CustomFileInput';
import CustomDatePicker from '@/components/common/form/CustomDatePicker';
import { getContractDocument } from '@/modules/serviceProposalHistory/api';
import { getProposalContractDocumentTypes as _getProposalContractDocumentTypes } from '@/modules/shared/code.api.js';
import {
  getProposalContractDocuments,
  uploadProposalContractDocument,
  removeProposalContractDocument,
} from '@/modules/billingHistory/api';
import alertMixin from 'utils/mixins/alert.mixin.js';

const PSF_1B_DOCUMENT_TYPE_ID = 11; // PSF 1 (b) – Renovación o Enmienda de Contratos

export default {
  name: 'ContractTab',
  components: { CustomFileInput, SectionTitle, CustomDatePicker },
  mixins: [alertMixin],
  props: {
    proposalRequestId: { type: Number, default: 0 },
    isAmendment: { type: Boolean, default: false },
    autoRemoveDocument: { type: Boolean, default: false },
  },
  data() {
    return {
      areDocumentsLoaded: false,
      documents: [],
    };
  },
  computed: {
    contractId() {
      return this.$route.params.contractId;
    },
    filteredDocuments() {
      return this.documents.filter((doc) => {
        if (this.isAmendment) {
          return doc.isAmendment && doc.typeId !== PSF_1B_DOCUMENT_TYPE_ID;
        }
        return !doc.isAmendment;
      });
    },
  },
  async mounted() {
    await this.getProposalContractDocumentTypes();
    const { data } = await getProposalContractDocuments(this.proposalRequestId);
    data.forEach((element) => {
      const doc = this.documents.find((d) => d.typeId == element.typeId);
      if (doc) {
        doc.attachmentId = element?.attachmentId || null;
        doc.attachmentName = element?.filename || null;
      }
    });
    this.areDocumentsLoaded = true;
  },
  methods: {
    async getProposalContractDocumentTypes() {
      const { data } = await _getProposalContractDocumentTypes();
      this.documents = data;
    },
    async removeFile(document) {
      if (this.autoRemoveDocument) {
        await removeProposalContractDocument(document.attachmentId);
      }

      this.$refs[`fileUpload-${document.typeId}`]?.[0]?.removeFile();
      document.attachment = null;
      document.attachmentId = null;
      document.attachmentName = null;
    },
    async downloadDocument(document) {
      const params = {
        contractId: this.contractId,
        proposalRequestId: this.proposalRequestId,
        date: document.date,
        type: document.typeId,
      };
      const response = await getContractDocument(params);
      _generalApi.ConvertToBlob(response.data);
    },
    async downloadSignedDocument(document) {
      if (!this.areDocumentsLoaded || !document.attachmentId) {
        this.$refs[`fileUpload-${document.typeId}`]?.[0]?.downloadFile();
      } else {
        await _generalApi.downloadDocumentByAttachmentId(document.attachmentId);
      }
    },
    async onUploadDocument(file, typeId) {
      if (!file) return;
      const doc = this.documents.find((d) => d.typeId === typeId);
      if (doc) doc.attachmentName = file.name;
      const formData = new FormData();
      formData.append('Document', file);
      formData.append('TypeId', typeId);
      formData.append('ProposalRequestId', this.proposalRequestId);
      await uploadProposalContractDocument(formData);
      this.ShowToast(
        '',
        'El documento ha sido subido de manera exitosa.',
        'success'
      );
    },
  },
};
</script>

<style lang="scss" scoped>
@import '~@/shared/scss/config/colors.scss';

.document__container {
  background-color: #f6f6f6;
}

.document-signed__container {
  display: flex;
  justify-content: end;
  padding-right: 27px;
}

.document-info {
  --bg-color: map-get($colors, 'secondary-3');
  background-color: var(--bg-color);
  padding: 10px 30px;

  &__description {
    display: flex;
    align-items: center;

    .content {
      margin-top: 10px;
    }
    .title {
      color: map-get($colors, 'secondary-6');
      font-size: 0.875rem;
    }
    .file-icon {
      color: map-get($colors, 'orange');
      margin-right: 0.9rem !important;
    }
    @media only screen and (max-width: 768px) {
      .title,
      .content {
        font-size: 0.8572rem;
      }
    }

    ::v-deep &__actions {
      margin-left: 0px;
      .show-icon i {
        margin-right: 0px !important;
      }

      .font-weight-normal {
        label {
          display: none;
        }
      }

      .upload-document {
        .file-select {
          cursor: pointer;
          span {
            font-size: 1rem;
            @media screen and (max-width: 768px) {
              font-size: 0.7857rem;
            }
          }
          em {
            @media screen and (max-width: 768px) {
              padding: 0.4286rem !important;
            }
          }
        }
      }

      .custom-upload-document {
        .file-input-container {
          background-color: map-get($colors, 'green');
          border: 1px solid map-get($colors, 'green');

          .file-select {
            cursor: pointer;
            span {
              display: none;
            }
            em {
              padding: 0px !important;
              color: map-get($colors, 'white');
            }
          }
        }
      }
    }
  }
}
</style>
